import { Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FileService, MessageService } from '@rubii/frontend-common';
import { Subscription } from 'rxjs';
// tslint:disable-next-line:no-require-imports
const uuidv4 = require('uuid').v4;
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss']
})
export class UploadComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedType: 'google' | 'excel' | null = null;
  selectedFile: File | null = null;
  uploadProgress = 0;
  isUploading = false;
  uploadComplete = false;
  uploadError: string | null = null;
  uploadedS3Key: string | null = null;
  private uploadUuid: string | null = null;
  private uploadExt: string | null = null;
  private readonly prefix = 'datasource-custom';
  private readonly folderId = '0';

  private uploadSubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private fileService: FileService,
    private messageService: MessageService<unknown>,
    private cdRef: ChangeDetectorRef,
  ) {
    this.initFileService();
  }

  ngOnInit(): void {}

  private initFileService(): void {
    const root = environment.fileRoot || '';
    if (!root) return;
    const base = root.endsWith('/') ? root : root + '/';
    this.fileService.init(
      base + 'file/create-multi-upload',
      base + 'file/pre-sign',
      base + 'file/abort-upload',
      base + 'file/complete-multi-upload',
    );
  }

  selectType(type: 'google' | 'excel'): void {
    this.selectedType = type;
    this.resetUpload();

    if (type === 'excel') {
      setTimeout(() => this.fileInput?.nativeElement?.click());
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const validExtensions = ['.xls', '.xlsx', '.csv'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(ext)) {
      this.uploadError = 'Please select a .XLS or .CSV file.';
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      this.uploadError = 'File size must be under 20MB.';
      return;
    }

    this.selectedFile = file;
    this.uploadError = null;
    this.uploadFile(file);
  }

  goToAnalysing(): void {
    sessionStorage.setItem('datasource-uploaded-file-path', this.uploadedS3Key || '');
    this.router.navigate(['/datasources/custom/analysing', this.uploadUuid]);
  }

  retryUpload(): void {
    this.uploadError = null;
    if (this.selectedFile) {
      this.uploadFile(this.selectedFile);
    }
  }

  private resetUpload(): void {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.isUploading = false;
    this.uploadComplete = false;
    this.uploadError = null;
    this.uploadedS3Key = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private async uploadFile(file: File): Promise<void> {
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadComplete = false;

    const ext = file.name.substring(file.name.lastIndexOf('.'));
    this.uploadExt = ext;
    const uuid = uuidv4();
    this.uploadUuid = uuid;
    const s3FileName = uuid + ext;
    const displayName = file.name;

    const timestamp = Date.now();
    const msgId = `file-upload-part-${this.prefix}-${s3FileName}-${this.folderId}-${timestamp}`;

    const partProgress = new Map<number, number>();
    let lastRenderedPercent = -1;

    this.uploadSubscription = this.messageService.msg.subscribe((ret: { id?: string; value?: { loaded: number; partNumber: number } }) => {
      if (ret?.id === msgId && ret.value) {
        partProgress.set(ret.value.partNumber, ret.value.loaded);
        let totalLoaded = 0;
        partProgress.forEach(v => totalLoaded += v);
        const pct = Math.min(100, Math.round(100 * totalLoaded / file.size));
        if (pct !== lastRenderedPercent) {
          lastRenderedPercent = pct;
          this.uploadProgress = pct;
          this.cdRef.detectChanges();
        }
      }
    });

    this.cdRef.detectChanges();

    try {
      const uploadId = await this.fileService.createMultiUpload(s3FileName, this.prefix, timestamp, this.folderId);
      const totalChunks = Math.ceil(file.size / this.fileService.chunkSize);
      const presignedUrls = await this.fileService.preSign(s3FileName, totalChunks, uploadId, this.prefix, timestamp, this.folderId);

      const chunkIndices = Array.from({ length: totalChunks }, (_, i) => i);
      const batchSize = 12;
      let parts: Array<{ ETag: string; PartNumber: number }> = [];

      for (let i = 0; i < chunkIndices.length; i += batchSize) {
        const batch = chunkIndices.slice(i, i + batchSize);
        const promises = batch.map(chunkIndex => {
          const offset = chunkIndex * this.fileService.chunkSize;
          const blob = file.slice(offset, Math.min(offset + this.fileService.chunkSize, file.size));
          return this.fileService.uploadPart(
            presignedUrls.get(chunkIndex) || '',
            chunkIndex + 1,
            blob,
            { ...file, name: s3FileName } as File,
            totalChunks,
            uploadId,
            this.prefix,
            timestamp,
            this.folderId,
          );
        });
        const batchParts = await Promise.all(promises);
        parts = [...parts, ...batchParts];
      }

      await this.fileService.completeMultiUpload(s3FileName, uploadId, parts, this.prefix, file.size, timestamp, this.folderId);

      this.uploadedS3Key = `${this.prefix}/${this.folderId}/${timestamp}/${s3FileName}`;
      this.isUploading = false;
      this.uploadComplete = true;
      this.uploadProgress = 100;

      // Keep display name for UI
      this.selectedFile = new File([file], displayName, { type: file.type });
    } catch (err) {
      console.error('File upload failed:', err);
      this.isUploading = false;
      this.uploadError = 'Upload failed. Please try again.';
      // Restore display name for retry
      this.selectedFile = new File([file], displayName, { type: file.type });
    } finally {
      if (this.uploadSubscription) {
        this.uploadSubscription.unsubscribe();
        this.uploadSubscription = null;
      }
      this.cdRef.detectChanges();
    }
  }
}
